import { useEffect, useState } from "react";
import { message } from "antd";
import { useGetInventoryQuery } from "../../../api/inventory/inventoryApi";
import { InventoryItem } from "../../../api/inventory/InventoryItem";
import { useGetDataMappingsQuery } from "../../../api/dataMapping/dataMappingApi";
import { useGetReservedQuery, usePostReservedMutation } from "../../../api/redemption/redemptionApi";
import { formatDateTimeISO8 } from "../../../utils/date";
import { ReservationPostReq } from "../../../api/redemption/ReservationPostReq";

export const TELECHOICE_PREFIX = "sb-esim-tc-"

/**
 * Auto-selects a Telechoice esim based on the confirmation code, that is
 * currently not assigned.
 */
export default function useTelechoice(orderId?: string, simIdx?: number, sku?: string, cancelFlag?: boolean, dt?: string, enabled = true) {
  const { data } = useGetInventoryQuery(undefined, { skip: !enabled });
  const { data: dataMapping, isFetching: dmIsFetch } = useGetDataMappingsQuery({
    vendorId: "sb",
  }, { skip: !enabled });
  const { data: reservedSim, isFetching: rIsFetch, error: rErr} = useGetReservedQuery({
    orderId: orderId ?? "",
    simIdx: simIdx ?? 0
  }, { skip: !enabled || !orderId });
  console.log("tc plugin: 23", orderId, simIdx, rIsFetch, reservedSim, rErr, cancelFlag);
  const [postReservationSim] = usePostReservedMutation();
  const [sel, setSel] = useState<InventoryItem>();
  const [isJustReserved, setIsJustReserved] = useState(false);
  useEffect(() => {
    async function run() {
      console.log("tc plugin: running", orderId, simIdx, sku);
      setSel(undefined);
      if (!enabled) {
        return;
      }
      if (sku === undefined || !sku.startsWith(TELECHOICE_PREFIX) || !data) {
        console.log("tc plugin: not fetching yet", sku, data);
        setSel(undefined);
        return;
      }
      if (dt === undefined) {
        console.log("tc plugin: missing dt restriction");
        return;
      }
      const isodate = formatDateTimeISO8(dt).split(" ")[0];
      const cutoff = "2025-08-30";

      if (cancelFlag) {
        console.log("tc plugin: not setting for canceled", cancelFlag);
        setSel(undefined);
        return;
      }
      if (!dataMapping || dmIsFetch || rIsFetch ||
          ((reservedSim?.ReservedOrderId !== orderId || reservedSim?.ReservedSimIdx !== simIdx) && !rErr)) {
        console.log("tc plugin: currently fetching, aborting", dmIsFetch, rIsFetch);
        setSel(undefined);
        return;
      }
      if (orderId == null || simIdx == null) {
        console.log("missing order id, sim idx");
        setSel(undefined);
        return;
      }
      if (reservedSim != null && reservedSim.ReservedOrderId === orderId && reservedSim.ReservedSimIdx === simIdx) {
        console.log("tc plugin: rserved sim alr avail", reservedSim);
        message.success("Loaded reserved esim");
        setSel(reservedSim);
        return;
      }

      if (isodate < cutoff) {
        console.log("tc plugin: ignoring from before 2025-08-30", isodate, cutoff);
        message.warning("Confirmation code selection unavailable for this redemption");
        return;
      }

      console.log("tc plugin: 56 attempting to select esim..");
      const vmap = dataMapping.Items.find(x => x.InternalSku === sku);
      if (!vmap) {
        message.error("No mapping found for SIMPLY SKU " + sku);
        console.log("No mapping found for SIMPLY SKU", sku, dataMapping);
        return;
      }
      if (!rErr) {
        console.log("tc plugin: waiting for error before proceeding");
        return;
      }
      const vsku = vmap.VendorSku;
      const telcSims = data.filter(x => x.VendorSku === vsku);
      console.log("telc sims", telcSims);
      const unused = telcSims.filter(x =>
        (x.Status === undefined || x.Status === "")
        && (x.RedemptionOrderId == null || x.RedemptionOrderId?.length === 0)
        && x.ExpiryDate > new Date().toISOString().split("T")[0]
        && x.ReservedOrderId == null && x.ReservedSimIdx == null
      );
      console.log("unused", unused, new Date().toISOString().split("T")[0]);
      unused.sort((a, b) => a.ExpiryDate.localeCompare(b.ExpiryDate));
      if (unused.length === 0) {
        message.error("Telechoice eSIMs for provider code " + vsku + " out of stock.");
        console.log(data);
        return;
      }
      const earliestExp = unused[0];
      const earliestExps = unused.filter(x =>
          x.ExpiryDate === earliestExp.ExpiryDate);
      const idx = Math.floor(Math.random() * earliestExps.length);
      const sim = earliestExps[idx];
      console.log("tc plugin: selected telechoice esim for vsku " + vsku, sim);


      console.log("tc plugin: found esim to reserve", sim);
      try {
        const reserv = await postReservationSim({
          orderId: orderId,
          simIdx: simIdx,
          iccid: sim["Iccid"]
        }).unwrap();
        console.log("tc plugin: Successfully reserved sim", reserv);
        if (reserv.ConfirmationCode != null) {
          message.success("Reserved eSIM with confirmation code " + reserv.ConfirmationCode);
        } else {
          message.success("Reserved eSIM with ICCID " + reserv.Iccid);
        }
        setSel(sim);
        setIsJustReserved(true);
      } catch (ex: any) {
        console.error("tc plugin: error reserving esim:", ex);

        if (ex.data?.message === "A reserved esim already exists") {
          return;
        }
        message.error("Error reserving eSIM");


      }
    }
    run();
  }, [data, sku, dataMapping, dmIsFetch, rIsFetch, reservedSim, orderId, postReservationSim, simIdx, cancelFlag, rErr, enabled]);

  /*
   * Only prerequisite required is SIM is not actually assigned (!= reserved)
   */
  function forceChangeReservation(newConfmCode: string) {
    if (!enabled) {
      return;
    }
    if (!orderId || !simIdx) {
      return;
    }
    if (!dataMapping || dmIsFetch || rIsFetch || !data ||
          ((reservedSim?.ReservedOrderId !== orderId || reservedSim?.ReservedSimIdx !== simIdx) && !rErr)) {
        console.log("tc plugin: currently fetching, aborting", dmIsFetch, rIsFetch);
        setSel(undefined);
        return null;
      }

    const vmap = dataMapping.Items.find(x => x.InternalSku === sku);
    if (!vmap) {
      message.error("No mapping found for SIMPLY SKU " + sku);
      console.log("No mapping found for SIMPLY SKU", sku, dataMapping);
      return;
    }
    const vsku = vmap.VendorSku;
    const telcSims = data.filter(x => x.VendorSku === vsku);
    console.log("telc sims", telcSims);
    const unused = telcSims.filter(x =>
      (x.RedemptionOrderId == null || x.RedemptionOrderId?.length === 0)
      && x.ExpiryDate > new Date().toISOString().split("T")[0]
    );

    const esimFound = unused.filter(x => x.ConfirmationCode === newConfmCode);
    if (esimFound.length === 0) {
      alert("No unused eSIM with matching SKU in inventory found with confirmation code " + newConfmCode);
      return;
    }
    const prr: ReservationPostReq = {
      iccid: esimFound[0].Iccid,
      orderId: orderId,
      simIdx: simIdx,
      force: true
    };
    postReservationSim(prr)
    .unwrap()
    .then(ret => {
      console.log("returned from force change", ret);
      alert("Updated reserved code");
      return ret;
    })
    .catch(err => {
      alert("Error updating reserved code: " + err);
    });


  }
  return { telcSim: sel, forceChangeReservation, isJustReserved };
}
